import { useCallback, useContext, useState } from 'react';
import { useAuthService } from '../../../services/auth/authService';
import { ResetPasswordController } from './interfaces';
import Swiper from 'swiper';
import { useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { AuthContext } from '../../../providers/authContext';
import { sleep } from '../../../utils/sleep';

export const useResetPasswordController = (): ResetPasswordController => {
  const [userId, setUserId] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const authService = useAuthService();
  const authContext = useContext(AuthContext);

  const navigation = useNavigate();

  const onSuccess = async userInfo => {
    setIsLoading(true);
    try {
      const idToken = userInfo?.credential;
      if (!idToken) {
        throw new Error('Google credential (idToken) is missing');
      }
      const { data: res } = await authService.externalLogin('google', {
        idToken,
      });
      if (!res.data.accessToken || !res.data.refreshToken) {
        setIsLoading(false);
        alert('Ocurrio un error');
        return;
      }

      const accessToken = res.data.accessToken;
      const refreshToken = res.data.refreshToken;

      const { data: userData } = await authService.findUserData(accessToken);

      await authContext.login(accessToken, refreshToken, userData.data._id);
      return;
    } catch (e: any) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const recoveryEmailSend = async (
    emailPassed: string,
    swiperController: Swiper
  ) => {
    setIsLoading(true);
    try {
      const { data: response } = await authService.forgotPassword(
        emailPassed.trim()
      );

      // Verificamos si se alcanzó el límite máximo de códigos
      if (!response.success) {
        if (response.message === 'Maximum code limit reached') {
          toast({
            status: 'error',
            title: 'Límite alcanzado',
            description:
              'Se alcanzó el límite máximo de intentos. Por favor, intente más tarde.',
            position: 'top-right',
            duration: 5000,
          });
        } else {
          toast({
            status: 'error',
            title: 'Ocurrió un error',
            description: response.message || 'No se pudo enviar el correo. Intentelo nuevamente.',
            position: 'top-right',
          });
        }
        return;
      }

      const userIdFromResponse = response.data.userId;

      if (!userIdFromResponse) {
        throw new Error('No se recibió el userId del servidor');
      }

      setUserId(userIdFromResponse);

      toast({
        status: 'success',
        title: 'Correo enviado',
        description:
          'Se envio un correo con el código de recuperacion a ' +
          emailPassed.trim(),
        position: 'top-right',
      });
      swiperController.slideNext();
    } catch (e) {
      console.log(e);
      toast({
        status: 'error',
        title: 'Ocurrió un error',
        description: 'No se pudo enviar el correo. Intentelo nuevamente.',
        position: 'top-right',
      });
    } finally {
      await sleep(200);
      setIsLoading(false);
    }
  };

  const postNewPassword = async (
    code: number,
    password: string,
    swiperController: Swiper
  ) => {
    setIsLoading(true);
    try {
      if (!userId) {
        throw new Error(
          'No se encontró el userId. Por favor, reinicie el proceso.'
        );
      }

      // Primero validamos el código
      const { data: validateResponse } = await authService.validateCode(
        userId,
        String(code)
      );

      // Verificamos que el código sea válido
      if (!validateResponse.success) {
        toast({
          status: 'error',
          title: 'Código inválido',
          description:
            'El código de seguridad es incorrecto. Verifique el código e intente nuevamente.',
          position: 'top-right',
        });
        return;
      }

      // Si la validación es exitosa, cambiamos la contraseña
      await authService.updatePassword(userId, password);

      toast({
        status: 'success',
        title: 'Contraseña actualizada',
        description: 'Se actualizó la contraseña exitosamente',
        position: 'top-left',
      });
      navigation('/auth');
    } catch (e: any) {
      console.log(e);
      if (e.response?.status === 400 || e.response?.status === 403) {
        toast({
          status: 'error',
          title: 'Código inválido',
          description:
            'El código de seguridad es incorrecto. Verifique el código e intente nuevamente.',
          position: 'top-right',
        });
      } else if (e.message?.includes('userId')) {
        toast({
          status: 'error',
          title: 'Sesión expirada',
          description:
            'Por favor, reinicie el proceso de recuperación de contraseña.',
          position: 'top-right',
        });
        swiperController.slideTo(0);
      } else {
        toast({
          status: 'error',
          title: 'Ocurrió un error',
          description:
            'No se pudo actualizar la contraseña. Intente nuevamente.',
          position: 'top-right',
        });
      }
    } finally {
      await sleep(200);
      setIsLoading(false);
    }
  };

  const passwordChecker = useCallback((password: string) => {
    const lowercaseRegExp = /(?=.*?[a-z])/;
    const digitsRegExp = /(?=.*?[0-9])/;
    const specialCharRegExp = /(?=.*[a-z])(?=.*[0-9])(?=.*[^A-Za-z0-9])/;
    const lowercasePassword = lowercaseRegExp.test(password);
    const digitsPassword = digitsRegExp.test(password);
    const specialCharPassword = specialCharRegExp.test(password);
    if (!lowercasePassword) {
      return 'screens.register.inputs.password.errors.lowercaseRequired';
    } else if (!digitsPassword) {
      return 'screens.register.inputs.password.errors.numberRequired';
    } else if (!specialCharPassword) {
      return 'screens.register.inputs.password.errors.specialCharacterRequired';
    }
    return '';
  }, []);

  const onFailure = res => {};

  const signIn = useGoogleLogin({
    onSuccess,
    flow: 'implicit',
  });

  return {
    example: 'example',
    recoveryEmailSend,
    postNewPassword,
    passwordChecker,
    isLoading,
    signIn,
    onSuccess,
    onFailure,
  };
};
